import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Engine } from "../lab/Engine";
import { defaults } from "../lab/modes";
import { labStore, downloadJSON } from "../lab/store";
import { TopHud } from "../components/lab/TopHud";
import { ControlPanel } from "../components/lab/ControlPanel";
import { BottomToolbar } from "../components/lab/BottomToolbar";


export default function InstrumentPage({ instrument: ins }) {
  const nav = useNavigate();
  const mode = ins.mode, host = useRef(null), eng = useRef(null);
  const live = labStore.live[ins.id], mobile = window.innerWidth < 768;
  const init = live?.settings || { density: mobile ? Math.min(ins.density, 72) : ins.density, pinHeight: ins.travel, speed: 1, material: "chrome", light: 1, simSpeed: 1, force: 1, paused: false, interact: true, colorize: true };
  const [params, setParams] = useState(live?.params || defaults(mode));
  const [settings, setSettings] = useState(init);
  const [tel, setTel] = useState(null);
  const [hist, setHist] = useState({ supported: true, canUndo: false, canRedo: false });
  const [panelOpen, setPanelOpen] = useState(true);
  const [view, setView] = useState("Isometric");
  const [fs, setFs] = useState(false);
  const [low, setLow] = useState(false);
  const stateRef = useRef({}); stateRef.current = { params, settings };

  useEffect(() => {
    const onParamUI = (k, v) => setParams((p) => ({ ...p, [k]: v }));
    const onAuto = (patch) => { setSettings((s) => ({ ...s, ...patch })); toast("Auto-tuned for this device: resolution lowered for smooth motion"); };
    const e = new Engine(host.current, { onTelemetry: setTel, onHistory: setHist, onParamUI, onAuto, settings: init, instrument: ins });
    eng.current = e; e.onQuality = (l) => l < 3 && toast("Auto-tuned graphics for smooth motion on this device"); e.setMode(mode, live?.params || defaults(mode)); e.restoreSnapshot(live?.snapshot);
    if (e.software) { setLow(true); setSettings((s) => ({ ...s, density: e.s.density })); }
    const onFs = () => setFs(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => {
      labStore.live[ins.id] = { ...stateRef.current, snapshot: e.snapshot() };
      document.removeEventListener("fullscreenchange", onFs); e.dispose();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setParam = useCallback((key, val) => { setParams((p) => ({ ...p, [key]: val })); eng.current.setParam(key, val); }, []);
  const setSetting = useCallback((key, val) => { setSettings((s) => ({ ...s, [key]: val })); eng.current.setSettings({ [key]: val }); }, []);
  const runAction = useCallback(async (id, payload) => {
    try { const msg = await eng.current.action(id, payload); if (msg) toast.success(msg); } catch (err) { toast.error(err.message || "Action failed"); }
  }, []);
  const openSaved = useCallback((s) => {
    setParams(s.params); setSettings(s.settings); eng.current.setSettings(s.settings); eng.current.setMode(mode, s.params); eng.current.restoreSnapshot(s.snapshot);
    toast.success(`Opened “${s.title}”`);
  }, [mode]);

  const actions = {
    lab: () => nav("/"),
    undo: () => eng.current.undo(), redo: () => eng.current.redo(),
    clear: () => { eng.current.clear(); toast("Experiment cleared"); },
    reset: () => { const d = defaults(mode); setParams(d); eng.current.resetExperiment(mode, d); setView("Isometric"); toast("Experiment reset"); },
    pause: () => setSetting("paused", !settings.paused),
    interact: () => setSetting("interact", !settings.interact),
    swap: () => setView(eng.current.swapPerspective()),
    png: () => { eng.current.exportPNG(); toast.success("Snapshot exported as PNG"); },
    save: () => {
      const n = labStore.saved.filter((x) => x.instrument === ins.id).length + 1;
      labStore.save({ instrument: ins.id, title: `${ins.code} · Experiment ${n}`, params, settings, snapshot: eng.current.snapshot() });
      toast.success(`Saved as ${ins.code} · Experiment ${n}`);
    },
    json: () => { downloadJSON({ lab: "Digital Matter Lab", instrument: ins.id, savedAt: new Date().toISOString(), params, settings }, `${ins.id}-${Date.now()}.json`); toast.success("Settings exported as JSON"); },
    fullscreen: () => (document.fullscreenElement ? document.exitFullscreen() : document.documentElement.requestFullscreen?.()),
    panel: () => setPanelOpen((o) => !o),
  };
  const actRef = useRef(actions); actRef.current = actions;
  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === "INPUT") return;
      const a = actRef.current;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") { e.preventDefault(); e.shiftKey ? a.redo() : a.undo(); }
      else if (e.code === "Space") { e.preventDefault(); a.pause(); }
      else if (e.key.toLowerCase() === "v") a.swap();
      else if (e.key === "Escape" && !document.fullscreenElement) a.lab();
    };
    window.addEventListener("keydown", onKey); return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className={`lab-root ${low ? "lab-lowpower" : ""} ${panelOpen ? "panel-open" : ""}`} data-testid="instrument-root" style={{ "--accent": ins.accent }}>
      <div className="lab-stage instrument-stage" data-testid="lab-stage">
        <div ref={host} className={`lab-canvas ${settings.interact ? "cursor-crosshair" : "cursor-grab"}`} data-testid="lab-canvas-host" />
        <div className="lab-vignette" /><div className="lab-noise" /><div className="lab-streak" />
        <div className="lab-frame" data-testid="lab-frame">
          <span className="br tl" /><span className="br tr" /><span className="br bl" /><span className="br brr" /><span className="ruler" />
          <div className="frame-readout" data-testid="frame-readout">
            INSTRUMENT <b>{ins.code}</b> · {view}<br />
            T+ <b>{tel ? tel.simT.toFixed(1) : "0.0"}s</b> · RES {tel ? Math.round(Math.sqrt(tel.pins)) : "—"}² · {ins.style.toUpperCase()}
          </div>
        </div>
        <BottomToolbar a={actions} settings={settings} hist={hist} fs={fs} panelOpen={panelOpen} />
      </div>
      <TopHud ins={ins} tel={tel} settings={settings} view={view} onLab={actions.lab} />
      <ControlPanel open={panelOpen} onClose={() => setPanelOpen(false)} ins={ins} mode={mode} params={params} setParam={setParam} runAction={runAction} settings={settings} setSetting={setSetting} onOpenSaved={openSaved} />
    </div>
  );
}
